import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBackIosOutlined";
import InboundIcon from "@material-ui/icons/ArrowDownwardOutlined";
import OutboundIcon from "@material-ui/icons/ArrowUpwardOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useMenuStyles from "../../components/menuStyles";

// Tela de eventos de UM conector: tudo que entrou/saiu (sem conteúdo).
const OUTCOME_LABEL = {
  none: "Nenhum destino encontrado",
  handoff: "Atendimento humano",
  mcp: "Navegador de IA (MCP)",
  sent: "Enviado",
};

const useStyles = makeStyles(theme => {
  const t = k => (theme.app ? theme.app[k] : undefined);
  return {
    wrap: { padding: "24px 32px", maxWidth: 980, margin: "0 auto" },
    back: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: t("textTertiary") || "#929AA4", cursor: "pointer", marginBottom: 10, "& svg": { fontSize: 13 } },
    title: { fontSize: 22, fontWeight: 800, color: t("heading") || "#1B222A" },
    desc: { fontSize: 13.5, color: t("textTertiary") || "#929AA4", margin: "4px 0 18px" },
    filters: { display: "flex", gap: 10, marginBottom: 16 },
    list: { display: "flex", flexDirection: "column", gap: 2 },
    row: { display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: `1px solid ${t("border") || "#EEF1F4"}` },
    dir: { width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, "& svg": { fontSize: 15 } },
    in: { background: "#EAF4F7", color: "#0b7285" },
    out: { background: "#EEF6EA", color: "#4a7a2b" },
    peer: { fontSize: 14, fontWeight: 600, color: t("ink") || "#2c3036", minWidth: 150 },
    arrow: { color: t("textTertiary") || "#929AA4", fontSize: 13 },
    label: { fontSize: 14, color: t("textSecondary") || "#5B6470", flex: 1 },
    link: { color: "#0b7285", cursor: "pointer", "&:hover": { textDecoration: "underline" } },
    none: { color: "#D14343", fontWeight: 600 },
    when: { fontSize: 12, color: t("textTertiary") || "#929AA4", whiteSpace: "nowrap" },
    empty: { textAlign: "center", color: t("textTertiary") || "#929AA4", padding: theme.spacing(8, 2), fontSize: 14 },
    more: { textAlign: "center", padding: "14px 0" },
  };
});

const ConnectorEvents = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { connectorId } = useParams();
  const [events, setEvents] = useState([]);
  const [connector, setConnector] = useState(null);
  const [dir, setDir] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (before) => {
    try {
      const params = { limit: 100 };
      if (dir) params.direction = dir;
      if (before) params.before = before;
      const { data } = await api.get(`/connectors/${connectorId}/events`, { params });
      setConnector(data.connector || null);
      setEvents(prev => (before ? [...prev, ...(data.events || [])] : data.events || []));
      setHasMore((data.events || []).length === 100);
    } catch (err) {
      toastError(err);
    }
  }, [connectorId, dir]);

  useEffect(() => {
    load();
  }, [load]);

  const labelOf = e => (OUTCOME_LABEL[e.outcome] ? OUTCOME_LABEL[e.outcome] : e.label || e.outcome);

  return (
    <div className={classes.wrap}>
      <span className={classes.back} onClick={() => history.push("/connectors")}><ArrowBackIcon /> Conectores</span>
      <div className={classes.title}>Eventos — {connector ? connector.name : "conector"}</div>
      <div className={classes.desc}>Tudo que entrou e saiu neste conector (sem o conteúdo da mensagem).</div>

      <div className={classes.filters}>
        <TextField
          select size="small" variant="outlined" label="Direção" value={dir} onChange={e => setDir(e.target.value)} style={{ minWidth: 170 }}
          SelectProps={{ MenuProps: { classes: { paper: menu.selectPaper }, getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" } } }}
        >
          <MenuItem value="">Entradas e saídas</MenuItem>
          <MenuItem value="in">Só entradas</MenuItem>
          <MenuItem value="out">Só saídas</MenuItem>
        </TextField>
      </div>

      {events.length === 0 ? (
        <div className={classes.empty}>Nenhum evento ainda neste conector.</div>
      ) : (
        <div className={classes.list}>
          {events.map(e => (
            <div key={e.id} className={classes.row}>
              <div className={`${classes.dir} ${e.direction === "out" ? classes.out : classes.in}`}>
                {e.direction === "out" ? <OutboundIcon /> : <InboundIcon />}
              </div>
              <span className={classes.peer}>{e.peer || "—"}</span>
              <span className={classes.arrow}>→</span>
              {e.link ? (
                <span className={`${classes.label} ${classes.link}`} onClick={() => history.push(e.link)}>{labelOf(e)}</span>
              ) : (
                <span className={`${classes.label} ${e.outcome === "none" ? classes.none : ""}`}>{labelOf(e)}</span>
              )}
              <span className={classes.when}>{new Date(e.at).toLocaleString("pt-BR")}</span>
            </div>
          ))}
        </div>
      )}

      {hasMore ? (
        <div className={classes.more}>
          <Button size="small" onClick={() => load(events[events.length - 1].id)}>Carregar mais</Button>
        </div>
      ) : null}
    </div>
  );
};

export default ConnectorEvents;
